import { useCallback, useState } from 'react';
import { createRoot } from 'react-dom/client';
import css from './configento.css?inline';
import { ConfiguratorRoot } from './ConfiguratorRoot';
import { publicApi, markReady, registerInstance } from './public-api';
import { adoptPreloadedRenderers } from './api/renderers';
import { readPreviewContext, type ConfiguratorPreviewContext } from './preview-context';
import { resolveWebshopConfig, type WebshopConfig } from './api/webshopConfig';
import { I18nProvider, resolveLocale, type Locale } from './i18n';
import { VERSION } from './version';
import { autoRestoreFromDeepLink, renderDeepLinkToolbar } from './deep-link';
import { fetchComponent } from './api/fetchComponent';
import type { V3ComponentResponse } from './api/types';

window.Configento = publicApi;

// Self-inject styles. The IIFE bundle is meant to be loaded with a single
// script tag — no separate <link rel="stylesheet"> required. We tag the
// <style> element so a re-load of the bundle replaces the previous copy
// instead of stacking duplicates.
if (typeof document !== 'undefined' && !document.querySelector('style[data-configento-styles]')) {
    const style = document.createElement('style');
    style.setAttribute('data-configento-styles', VERSION);
    style.textContent = css;
    document.head.appendChild(style);
}

// Multi-instance discovery: every element carrying `data-configento` is a mount target.
// The element's id (typically `configento-{hash}`) is informational — we use the
// data-attribute as the contract so customers may bring their own id scheme.
// Renderers the page stored before this script ran, so the first render
// already uses them.
adoptPreloadedRenderers();

const mountTargets = document.querySelectorAll<HTMLElement>('[data-configento]');
const previewContext = readPreviewContext();

// UI locale resolution: preview-context override (admin / plugin) →
// HTML5-snippet `data-configento-locale` → generic `data-locale` →
// browser → en_US fallback. Same locale applies to every instance on
// the page; multi-instance per-instance locale overrides would need
// a per-mount i18n root — not in scope for the current phase.
//
// `data-configento-locale` reads via `dataset.configentoLocale` (JS
// hyphen-to-camelCase). Merchants set that in the HTML5 embed snippet
// alongside data-configento-token / data-component-id, expecting it
// to drive both bundle chrome and content — same source resolveWebshopConfig
// reads, hoisted up so the I18nProvider knows before any component renders.
const uiLocale = resolveLocale(
    previewContext?.uiLocale ??
        mountTargets[0]?.dataset.configentoLocale ??
        mountTargets[0]?.dataset.locale ??
        undefined,
);

if (mountTargets.length === 0) {
    console.warn(
        '[Configento] no [data-configento] mount target found — bundle loaded but not mounted',
    );
} else {
    mountTargets.forEach((element, index) => {
        const id = element.id || `configento-anon-${index}`;
        const config = resolveWebshopConfig(element, previewContext);
        const componentId = config?.componentId ?? element.dataset.componentId ?? null;

        // Register the public-API record FIRST. Methods start as throwing
        // stubs; ConfiguratorRoot calls `attachInstanceApi(id, …)` once the
        // Component has finished loading so plugin callers can hold a
        // stable reference across the load boundary.
        registerInstance({ id, componentId, element, config });

        // Plain HTML5 embeds don't set `window._configentoPreview`; the
        // config lives on the mount element as `data-configento-*`. Promote
        // that to a preview context so the ConfiguratorRoot auto-fetches on
        // mount — the legacy html5.js loaded on script include, merchants
        // expect the same reflex. When both preview AND config are set,
        // preview wins (admin iframe / plugin flow keeps working).
        const effectivePreview = previewContext ?? promotePreviewFromConfig(config);

        // Hold the configurator's height before anything renders, so the page
        // below it does not jump when the first step appears.
        if (config?.minHeight) element.style.minHeight = config.minHeight;

        // Start token + Component loading now, in parallel with React's first
        // render, instead of from an effect after it.
        const componentRequest = startComponentRequest(effectivePreview, config);

        const root = createRoot(element);
        root.render(
            <Mount
                preview={effectivePreview}
                instanceId={id}
                bootLocale={uiLocale}
                componentRequest={componentRequest}
            />,
        );

        console.info(
            `[Configento] v${VERSION} mounted on #${id} (locale ${uiLocale})` +
                (previewContext
                    ? ` (preview, component ${previewContext.componentId})`
                    : effectivePreview
                      ? ` (html5-embed, component ${effectivePreview.componentId})`
                      : ''),
        );
    });
}

/**
 * Owns the locale for one mounted instance.
 *
 * The locale used to be fixed at boot and handed straight to the
 * I18nProvider. It has to be state as soon as the language gate exists: the
 * visitor's pick has to move the bundle's own strings ("Total", "Next") as
 * well as the Component's titles, and only a re-render with a new provider
 * value does both.
 *
 * `picked` stays undefined until someone actually chooses, which keeps the
 * existing content-locale precedence intact — an embed that sets
 * `data-configento-locale` and no gate behaves exactly as before.
 */
function Mount({
    preview,
    instanceId,
    bootLocale,
    componentRequest,
}: {
    preview: ConfiguratorPreviewContext | undefined;
    instanceId: string;
    bootLocale: Locale;
    componentRequest: Promise<V3ComponentResponse> | undefined;
}) {
    // The Component's spelling (`en_GB`), which only the content lookup wants.
    const [picked, setPicked] = useState<string | undefined>(undefined);
    // The bundle's own strings exist for six locales, so a picked `en_GB` has
    // to come back through resolveLocale to find them under `en_US`.
    const chromeLocale = picked ? resolveLocale(picked) : bootLocale;
    const resetPicked = useCallback(() => setPicked(undefined), []);

    return (
        <I18nProvider locale={chromeLocale}>
            <ConfiguratorRoot
                preview={preview}
                instanceId={instanceId}
                componentRequest={componentRequest}
                pickedLocale={picked}
                onLocalePicked={setPicked}
                onLocaleReset={resetPicked}
            />
        </I18nProvider>
    );
}

markReady();

// If the page URL carries a saved-configuration deep link
// (`?dl=<uuid>` or `?configento_asset=<uuid>`), auto-restore the
// referenced asset into the first mounted configurator once the live
// API is attached. Legacy V3 storefronts (mail-share flows) rely on
// this reflex; do NOT gate it behind a config flag — the sending
// merchant expects the recipient's page to open with the saved state
// already applied.
autoRestoreFromDeepLink();
renderDeepLinkToolbar();

/**
 * The Component this mount will render: the one the host embedded in its
 * config, or a fetch started right away. Undefined without a preview context
 * (nothing to load). The rejection is marked handled here because the
 * consumer only attaches its handlers after React's first commit.
 */
function startComponentRequest(
    preview: ConfiguratorPreviewContext | undefined,
    config: WebshopConfig | null,
): Promise<V3ComponentResponse> | undefined {
    if (!preview) return undefined;
    const embedded = config?.component;
    if (embedded && typeof embedded === 'object' && 'data' in embedded) {
        return Promise.resolve(embedded as V3ComponentResponse);
    }
    if (embedded !== undefined) {
        console.warn('[Configento] embedded component has no "data" — fetching instead');
    }
    const request = fetchComponent(preview);
    request.catch(() => {});
    return request;
}

/**
 * Turn a data-attribute WebshopConfig into the ConfiguratorPreviewContext
 * the fetch pipeline consumes. Used only for plain HTML5 embeds — the
 * admin iframe and storefront plugins keep setting `window._configentoPreview`
 * directly (they carry richer state like `embedMode: 'storefront'`,
 * `cmsBlocks`, `combinedImageMount`).
 *
 * Requires at minimum a `componentId`; everything else has sensible
 * fallbacks. A missing `token` (empty string) is legal when the merchant
 * uses `tokenEndpoint` for short-lived tokens — the resolver picks it up.
 * A missing `customer` fails at fetch time with a clear auth error, better
 * than the silent "waiting for data" placeholder that hid the config gap.
 */
function promotePreviewFromConfig(
    config: WebshopConfig | null,
): ConfiguratorPreviewContext | undefined {
    if (!config?.componentId) return undefined;
    return {
        componentId: config.componentId,
        customer: config.customer ?? '',
        token: config.token ?? '',
        tokenEndpoint: config.tokenEndpoint,
        cloudBaseUrl: config.cloudBaseUrl ?? 'cloud.configento.app',
        assetBaseUrl: config.assetBaseUrl,
        // No `data-configento-tax-rate` → no host rate: prices keep the
        // basis they were authored on. Defaulting to '0' converted gross
        // prices to net on every snippet embed that left the attribute out.
        taxRate: config.taxRate !== undefined ? String(config.taxRate) : '',
        currency: config.currency,
        currencyRates: config.currencyRates,
        frontendVersion: config.frontendVersion ?? 'v3',
        uiLocale: config.uiLocale,
        contentLocale: config.contentLocale,
        // Data-attribute embeds are storefront-side by definition (a real
        // shop page). `embedMode: 'storefront'` strips the admin-preview
        // chrome (BottomBar, inline SummaryPanel) so the merchant's own
        // theme components own those slots.
        embedMode: 'storefront',
        languageSelect: config.languageSelect,
        languageSelectTitle: config.languageSelectTitle,
        languages: config.languages,
        productAttributes: config.productAttributes,
        cmsBlocks: config.cmsBlocks,
    };
}
